import { cents, jobBalance, type Balance, type Cents } from "@roof-hawk/core";

import type { fetchProductionBoard } from "../../lib/queries";

/** What `fetchProductionBoard` hands the board. */
export type ProductionBoardData = Awaited<
  ReturnType<typeof fetchProductionBoard>
>;

/**
 * What one card on the production board says the job still owes.
 *
 * Its own function, not inline in the component, so the test runs this — the
 * mapping the screen uses — rather than a copy of it. A copy stayed green
 * through four reverts of the real one (review rounds 3 and 4 of #233).
 * `jobBalance` in packages/core does the arithmetic; this only decides which
 * of the board's fields reach it.
 */
export function boardBalanceFor(
  board: ProductionBoardData | null | undefined,
  jobId: string,
  /**
   * The shell's figure, not the board's `approx_contract` — that view is a
   * rough size for deciding whether a deposit is needed, and it does not know
   * about the GC fee.
   */
  contract: Cents,
): Balance {
  const first = board?.firstPayments[jobId];
  const signed = board?.signedContracts?.[jobId];
  return jobBalance({
    contract,
    firstCents: first == null ? null : cents(first),
    // What they signed, so the deposit's default does not move when a change
    // order is approved and the Final takes the whole of it (ADR 0205).
    signedContractCents: signed == null ? null : cents(signed),
    // The draws on a job sold in four goes. Without them a customer who had
    // paid three instalments still read as owing three quarters of the job,
    // and the final payment was worked out from the wrong base.
    middleCents: Object.fromEntries(
      Object.entries(board?.middlePayments[jobId] ?? {}).map(
        ([slot, amount]) => [slot, cents(amount)],
      ),
    ),
    paidSlots: board?.paidSlots[jobId] ?? [],
    // What each of those slots actually billed. Slot 4 does not bill the Final
    // row — see `raiseBalanceRequest` — so without this the board reads a job
    // paid off in one go as still owing its deposit.
    billedBySlot: Object.fromEntries(
      Object.entries(board?.billedBySlot[jobId] ?? {}).map(([slot, amount]) => [
        slot,
        cents(amount),
      ]),
    ),
    // Money the customer has sent that the bank has not seen. The board said
    // "Owes $13,260" on a job whose deposit was paid by card on Friday while
    // the letter asked for $6,630 (Josh, 2026-09-14).
    onItsWay: cents(
      (board?.onItsWay[jobId] ?? 0) +
        (board?.plans?.[jobId]?.planOnItsWayCents ?? 0),
    ),
    // Monthly checks toward an in-house payment plan (ADR 0286).
    planPaidCents: cents(board?.plans?.[jobId]?.planPaidCents ?? 0),
    // Money that settled no scheduled payment — a link raised by hand, most
    // often (ADR 0141). Computed by `fetchProductionBoard` and, until
    // 2026-09-14, never passed on: the board went on showing the whole balance
    // on a job whose customer had paid half of it by link (Josh, 2026-09-13).
    offScheduleCents: cents(board?.offSchedule[jobId] ?? 0),
  });
}
